import { Card } from "primereact/card";
import React, { useEffect, useState } from "react";
import {
  Button,
  Select,
  Textfield,
  Tooltip,
} from "../../../../../components/core";
import { Divider } from "primereact/divider";
import { formatDateTime } from "../../../../../algorithms/date";
import { useQuery } from "@apollo/client";
import { FIND_AGENT_TOPUP_SOURCE } from "../../../../../constants/graphql/partners-query";
import { Strings } from "../../../../../constants/strings";

const FUND_SOURCE_OPTIONS = [
  { value: 0, name: "None" },
  { value: 2, name: "Partner Wallet" },
  { value: 3, name: "Partner Branch Wallet" },
  { value: 4, name: "Partner Agent Wallet" },
];

const PartnerWalletConfiguration = ({
  partnerProfileData,
  handleOnChange,
  accessPermission,
  changesMade,
  onSubmit,
  onCancel,
  setValidation,
  handleCancelChanges,
  handleSaveChanges,
  goToNextStep,
  isAlreadyExisting,
  setActiveIndex,
  isLoading,
}) => {
  const formattedDate = formatDateTime(new Date());
  // local variables
  const [topUpOptions, setTopUpOptions] = useState([]);

  // gql
  const allAgentTopUpSource = useQuery(FIND_AGENT_TOPUP_SOURCE);

  const handleSubmit = () => {
    if (onSubmit) {
      onSubmit();
    } else {
      handleValidateFields();
    }
  };
  const showValidationError = (message) => {
    setValidation({
      icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
      title: Strings.popup_error_title,
      message,
      open: true,
      actions: (
        <div className="grid grid-cols-1 gap-2">
          <Button
            value={"OK"}
            className={"p-2"}
            onClick={() => setValidation(false)}
          />
        </div>
      ),
    });
  };

  console.log({ partnerProfileData });

  const handleValidateFields = () => {
    // If PARTNER_WALLET is selected, topup rules don't apply — skip them
    if (parseInt(partnerProfileData.agentSourceFundNo) === 2) {
      return showConfirmation();
    }

    // Check if agent topup source is selected
    if (
      !partnerProfileData.agentTopupSourceNo ||
      partnerProfileData.agentTopupSourceNo === -1 ||
      partnerProfileData.agentTopupSourceNo === "-1"
    ) {
      return showValidationError("Please select a valid agent topup source");
    }

    // Skip amount validations if topup source is -2
    if (
      partnerProfileData.agentTopupSourceNo === -2 ||
      partnerProfileData.agentTopupSourceNo === "-2"
    ) {
      return showConfirmation();
    }

    // check if topup amount is less than the minimum(50)
    if (parseInt(partnerProfileData.agentTopupAmount) < 50) {
      return showValidationError(
        "Invalid amount. Amount should be greater than ₱50.00",
      );
    }

    if (parseInt(partnerProfileData.agentTopupAmount) % 50 !== 0) {
      return showValidationError(
        "Topup amount must be in increments of ₱50.00",
      );
    }

    // Check if threshold is provided and valid
    if (
      !partnerProfileData.agentTopupThreshold ||
      parseFloat(partnerProfileData.agentTopupThreshold) <= 0
    ) {
      return showValidationError(
        "Agent topup threshold must be greater than 0",
      );
    }

    // Check if topup amount is provided and valid
    if (
      !partnerProfileData.agentTopupAmount ||
      parseFloat(partnerProfileData.agentTopupAmount) <= 0
    ) {
      return showValidationError("Agent topup amount must be greater than 0");
    }

    // Validate that topup amount is not greater than threshold
    if (
      parseFloat(partnerProfileData.agentTopupAmount) >
      parseFloat(partnerProfileData.agentTopupThreshold)
    ) {
      return showValidationError(
        "Agent topup amount cannot be greater than the threshold",
      );
    }

    // All validations passed, show confirmation
    showConfirmation();
  };

  const showConfirmation = () => {
    setValidation({
      icon: `${process.env.PUBLIC_URL}/assets/images/check.png`,
      title: Strings.popup_confirm_save_title,
      message: Strings.popup_confirm_save_massage,
      open: true,
      actions: (
        <div className="grid grid-cols-2 gap-2">
          <Button
            type="button"
            onClick={() => {
              setValidation({
                open: false,
              });
            }}
            disabled={isLoading}
            value={"No"}
            className={"p-2"}
          />
          <Button
            value={"Yes"}
            loading={isLoading}
            className={"p-2"}
            onClick={handleSaveChanges}
          />
        </div>
      ),
    });
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      setValidation({
        icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
        title: Strings.popup_confirm_cancel_title,
        message: Strings.popup_confirm_cancel_massage,
        open: true,
        actions: (
          <div className="grid grid-cols-2 gap-2">
            <Button
              onClick={() => setValidation({ open: false })}
              value={"No"}
              className={"p-2 w-full"}
            />
            <Button
              onClick={handleCancelChanges}
              value={"Yes"}
              className={"p-2 w-full"}
            />
          </div>
        ),
      });
    }
  };

  const formatValue = (value) => {
    const number = parseFloat(value);
    if (isNaN(number)) return value;

    const formatted = number.toLocaleString("en-PH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return `${formatted}`;
  };

  // useEffects
  useEffect(() => {
    if (allAgentTopUpSource && allAgentTopUpSource.data) {
      const optn = allAgentTopUpSource.data.allPartnerAgentTopupSource.map(
        (val) => {
          return {
            value: val.value === 0 ? -2 : val.value,
            name: val.label !== "None" ? `${val.label} Wallet` : val.label,
          };
        },
      );
      setTopUpOptions(optn);
    }
  }, [allAgentTopUpSource]);

  // // TODO: fix this :>
  // useEffect(() => {
  //   if (isAlreadyExisting) {
  //     setActiveIndex(1);
  //   }
  // }, [isAlreadyExisting]);
  return (
    <Card className="bg-slate-50 font-montserrat text-secondary-purpleGray">
      <div className="font-montserrat grid grid-cols-12 space-y-2">
        <div className="font-bold text-xl col-span-12">
          <span className="pi pi-wallet pr-4"></span>
          Partner Wallet Configuration
        </div>

        <div className="col-span-12">
          <div className="grid grid-cols-12 items-center space-x-2">
            <div className="col-span-3">Current Partner Wallet Balance</div>
            <div className="col-span-3">
              <Textfield
                prefix={"₱"}
                disabled
                value={partnerProfileData.walletBalance || 0}
              />
            </div>
            <span className="col-span-4 italic text-xs text-blue-500">
              as of {formattedDate || ""}
            </span>
          </div>
        </div>

        <div className=" px-4 col-span-12">
          <Divider className="border border-gray-200" />
        </div>

        {/* <div className="col-span-12">
          This partner wallet configuration will serve as the agent's reference
          for automatic top-ups
        </div> */}

        <div className="col-span-12 text-[18px] font-semibold">
          {/* All couriers under this partner will follow this as reference for
          automatic top-ups */}
          Partner Automatic Topup Rules
        </div>

        {/* Agent Fund Source */}
        <div className="col-span-12">
          <div className="grid grid-cols-12 items-center space-x-2">
            <div className="col-span-3">
              <Tooltip
                message="Defines the wallet source that agents under this partner draw funds from."
                className="pr-2"
              />
              Agent Fund Source
            </div>
            <div className="col-span-3">
              <Select
                items={FUND_SOURCE_OPTIONS}
                defaultOption={{ value: -1, name: "-- Select fund source --" }}
                value={partnerProfileData.agentSourceFundNo ?? 0}
                name="agentSourceFundNo"
                onChange={handleOnChange}
              />
            </div>
          </div>
        </div>

        {/* If PARTNER_WALLET is selected, topup rules do not apply */}
        {parseInt(partnerProfileData.agentSourceFundNo) === 0 ? null : parseInt(
            partnerProfileData.agentSourceFundNo,
          ) === 2 ? (
          <div className="col-span-12 bg-blue-50 border border-blue-200 rounded-md p-3 text-sm text-blue-700">
            <span className="font-semibold">Partner Wallet selected:</span> Auto
            topup rules do not apply. The partner wallet will directly fund
            agent transactions.
          </div>
        ) : (
          <>
            <div className="col-span-12">
              <div className="grid grid-cols-12 items-center space-x-2">
                <div className="col-span-3">Topup Source</div>
                <div className="col-span-3">
                  <Select
                    items={topUpOptions}
                    defaultOption={{
                      value: -1,
                      name: "-- Select topup source --",
                    }}
                    value={
                      partnerProfileData.agentTopupSourceNo === 0
                        ? -2
                        : partnerProfileData.agentTopupSourceNo
                    }
                    name={`agentTopupSourceNo`}
                    onChange={(e) => {
                      handleOnChange(e);
                      // Clear threshold and amount when source changes
                      handleOnChange({
                        target: { name: "agentTopupThreshold", value: 0 },
                      });
                      handleOnChange({
                        target: { name: "agentTopupAmount", value: 0 },
                      });
                    }}
                  />
                </div>
                <div className="col-span-4"></div>
              </div>
            </div>

            <div className="col-span-12">
              <div className="grid grid-cols-12 items-center space-x-2">
                <div className="col-span-3 items-center">
                  <Tooltip
                    message={
                      "When courier wallet reaches this amount, top up amount will automatically be deducted from the selected source"
                    }
                    className={`pr-2`}
                  />{" "}
                  Auto Trigger Topup Threshold
                </div>
                <div className="col-span-3">
                  <Textfield
                    isNumberOnly={true}
                    prefix={"₱"}
                    disabled={
                      partnerProfileData.agentTopupSourceNo === -1 ||
                      partnerProfileData.agentTopupSourceNo === "-1" ||
                      partnerProfileData.agentTopupSourceNo === -2 ||
                      partnerProfileData.agentTopupSourceNo === "-2"
                    }
                    value={partnerProfileData.agentTopupThreshold}
                    name={`agentTopupThreshold`}
                    onChange={(e) => {
                      const value = e.target.value;
                      var numberPattern = /^\d*\.?\d{0,2}$/;
                      if (value === "" || numberPattern.test(value)) {
                        if (value === "" || parseFloat(value) <= 1000000000) {
                          handleOnChange(e);
                        }
                      }
                    }}
                  />
                </div>
                <div className="col-span-5 text-xs italic text-red-500">
                  triggers when balance reaches or falls below this amount
                </div>
              </div>
            </div>

            <div className="col-span-12">
              <div className="grid grid-cols-12 items-center space-x-2">
                <div className="col-span-3">
                  <Tooltip
                    message={
                      "When courier wallet reaches the topup threshold, this amount will automatically be deducted from the selected source wallet"
                    }
                    className={`pr-2`}
                  />{" "}
                  Topup Amount
                </div>
                <div className="col-span-3">
                  <Textfield
                    prefix={"₱"}
                    disabled={
                      partnerProfileData.agentTopupSourceNo === -1 ||
                      partnerProfileData.agentTopupSourceNo === "-1" ||
                      partnerProfileData.agentTopupSourceNo === -2 ||
                      partnerProfileData.agentTopupSourceNo === "-2"
                    }
                    value={partnerProfileData.agentTopupAmount}
                    name={`agentTopupAmount`}
                    onChange={(e) => {
                      const value = e.target.value;
                      var numberPattern = /^\d*\.?\d{0,2}$/;
                      if (value === "" || numberPattern.test(value)) {
                        if (value === "" || parseFloat(value) <= 1000000000) {
                          handleOnChange(e);
                        }
                      }
                    }}
                  />
                </div>
                <div className="col-span-5 text-xs italic text-red-500">
                  amount to be added to the courier's MBOX wallet when triggered
                  (must be in increments of ₱50)
                  {partnerProfileData.agentTopupAmount &&
                    parseFloat(partnerProfileData.agentTopupAmount) % 50 !==
                      0 && (
                      <div className="font-semibold mt-1">
                        ⚠ Amount must be in increments of ₱50
                      </div>
                    )}
                </div>
              </div>
            </div>

            {/* selection preview */}
            {partnerProfileData.agentTopupSourceNo &&
            partnerProfileData.agentTopupSourceNo !== -1 &&
            partnerProfileData.agentTopupSourceNo !== "-1" ? (
              <div className="col-span-12 bg-primary-primaryYellowLight border-2 border-slate-300 rounded-[5px]">
                <div className="grid grid-cols-7 p-3 items-center">
                  <span className="col-span-2 font-bold">
                    Detailed Explanation:
                  </span>

                  <span className="col-span-5">
                    {partnerProfileData.agentTopupSourceNo === -2 ||
                    partnerProfileData.agentTopupSourceNo === "-2" ? (
                      <>
                        No automatic top-up will be applied. Couriers under this
                        partner will need to manually top-up their wallets or
                        use alternative funding methods.
                      </>
                    ) : (
                      <>
                        <b>
                          ₱ {formatValue(partnerProfileData.agentTopupAmount)}
                        </b>{" "}
                        will automatically be deducted from{" "}
                        <b>
                          {topUpOptions.find(
                            (option) =>
                              option.value ===
                              parseInt(partnerProfileData.agentTopupSourceNo),
                          )?.name || partnerProfileData.agentTopupSourceNo}
                        </b>{" "}
                        once the courier's MBOX wallet balance reaches{" "}
                        <b>
                          ₱{" "}
                          {formatValue(partnerProfileData.agentTopupThreshold)}
                        </b>{" "}
                        or below{" "}
                      </>
                    )}
                  </span>
                </div>
              </div>
            ) : null}
          </>
        )}

        {/* action buttons */}
        <div className="col-span-12 pt-9 ">
          <div className="flex justify-between items-center">
            {" "}
            <div className="items-start justify-start flex col-span-4">
              <Button
                type="button"
                disabled={changesMade}
                className="p-2 col-span-2 bg-primary-primaryYellowLight"
                // onClick={handleCancel}
                value="Previous Page"
                size="small"
                // iconPos="right"
                icon="pi pi-angle-double-left"
                onClick={() => {
                  setActiveIndex(0);
                }}
              />
            </div>
            {/* <div className=""> */}
            <div className="flex gap-2 ">
              <Button
                type="button"
                disabled={
                  !changesMade ||
                  accessPermission.partners === 1 ||
                  accessPermission.partners === "1"
                }
                value={`Cancel`}
                className="p-2 col-span-2"
                onClick={handleCancel}
              />
              <Button
                type="submit"
                // disabled={!changesMade}
                disabled={
                  !changesMade ||
                  accessPermission.partners === 1 ||
                  accessPermission.partners === "1"
                }
                value={`Save Changes`}
                className="p-2 col-span-2"
                onClick={handleSubmit}
              />
            </div>
            <div className="items-end justify-end flex col-span-4">
              <Button
                type="button"
                disabled={changesMade}
                className="p-2 col-span-2 bg-primary-primaryYellowLight"
                // onClick={handleCancel}
                value="Next Page"
                size="small"
                iconPos="right"
                icon="pi pi-angle-double-right"
                onClick={() => {
                  // handleEditOP();
                  goToNextStep();
                }}
              />
            </div>
          </div>
          {/* </div> */}
        </div>
      </div>
    </Card>
  );
};

export default PartnerWalletConfiguration;
